<template>
  <div class="retrospect-chart-container" :style="{ flexDirection: legendPosition === 'right' ? 'row' : 'column' }">
    <div class="chart-wrapper" :style="{ width: (legendPosition === 'external' ? numericSize + 300 : size) + 'px', height: (legendPosition === 'external' ? numericSize + 120 : size) + 'px' }">
      <svg
        :width="legendPosition === 'external' ? numericSize + 300 : size"
        :height="legendPosition === 'external' ? numericSize + 120 : size"
        :viewBox="legendPosition === 'external' ? `-150 -60 ${numericSize + 300} ${numericSize + 120}` : `0 0 ${size} ${size}`"
        preserveAspectRatio="xMidYMid meet"
        :aria-label="ariaLabel || 'Gráfico de distribuição'"
        role="list"
      >
        <g :transform="`translate(${size / 2}, ${size / 2})`">
          <g
            v-for="(slice, index) in processedData"
            :key="slice.key || index"
            class="chart-slice"
            role="listitem"
            :aria-label="`${slice.label}: ${slice.percent}%`"
            @mouseenter="hoveredIndex = index; $emit('slice-hover', slice.original)"
            @mouseleave="hoveredIndex = -1"
            @click="onSliceClick(slice)"
          >
            <path
              :d="slice.path"
              :fill="slice.color"
              :class="{ 'slice-hovered': hoveredIndex === index }"
            />
            
            <!-- Label Interno (Porcentagem) -->
            <text
              v-if="showPercentLabel && legendPosition === 'inside' && slice.percent > 5"
              :x="slice.labelX"
              :y="slice.labelY"
              text-anchor="middle"
              dominant-baseline="middle"
              class="slice-label"
              pointer-events="none"
            >
              <tspan :x="slice.labelX" dy="-0.6em">{{ slice.label }}</tspan>
              <tspan :x="slice.labelX" dy="1.2em" font-weight="bold">{{ slice.percent }}%</tspan>
            </text>
             <!-- Label Interno Simplificado se não houver espaço para quebra de linha -->
             <text
              v-else-if="showPercentLabel && legendPosition === 'inside' && slice.percent > 3 && slice.percent <= 5"
               :x="slice.labelX"
              :y="slice.labelY"
              text-anchor="middle"
              dominant-baseline="middle"
              class="slice-label small"
              pointer-events="none"
             >
                {{ slice.percent }}%
             </text>
          </g>
          
          <!-- Círculo Central para efeito Donut -->
          <circle
            v-if="innerRadius > 0"
            cx="0"
            cy="0"
            :r="innerRadius"
            fill="#FFFFFF"
          />
          
          <!-- Legendas Externas -->
          <template v-if="legendPosition === 'external'">
            <g
              v-for="(slice, index) in processedData"
              :key="'legend-' + (slice.key || index)"
              class="external-legend"
              @mouseenter="hoveredIndex = index; $emit('slice-hover', slice.original)"
              @mouseleave="hoveredIndex = -1"
              @click="onSliceClick(slice)"
            >
              <!-- Círculo colorido -->
              <circle
                :cx="slice.legendX"
                :cy="slice.legendY"
                r="6"
                :fill="slice.color"
                class="legend-color-circle"
              />
              <!-- Texto da legenda -->
              <text
                :x="slice.legendTextX"
                :y="slice.legendY"
                :text-anchor="slice.legendTextAnchor"
                dominant-baseline="middle"
                class="external-legend-text"
                pointer-events="none"
              >
                <tspan>{{ slice.label }}</tspan>
                <tspan :x="slice.legendTextX" dy="1.2em" class="legend-percent-text">{{ slice.percent }}%</tspan>
              </text>
            </g>
          </template>
        </g>
      </svg>
    </div>

    <!-- Legenda Lateral -->
    <div v-if="legendPosition === 'right'" class="chart-legend">
      <ul role="list">
        <li
          v-for="(item, index) in processedData"
          :key="item.key || index"
          class="legend-item"
          @mouseenter="hoveredIndex = index"
          @mouseleave="hoveredIndex = -1"
          @click="onSliceClick(item)"
        >
          <span class="legend-color" :style="{ backgroundColor: item.color }"></span>
          <span class="legend-text">
            <strong>{{ item.label }}</strong>
            <span class="legend-percent">{{ item.percent }}%</span>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RetrospectChart',
  props: {
    data: {
      type: Array,
      required: true,
      // Esperado: [{ key, label, value, color? }]
    },
    size: {
      type: [Number, String],
      default: 360,
    },
    innerRadius: {
      type: Number,
      default: function () {
        // Padrão 45% do raio (size / 2 * 0.45)
        return (parseInt(this.size) / 2) * 0.45;
      },
    },
    showPercentLabel: {
      type: Boolean,
      default: true,
    },
    legendPosition: {
      type: String,
      default: 'inside', // 'inside' | 'right' | 'external'
      validator: (val) => ['inside', 'right', 'external'].includes(val),
    },
    sortData: {
      type: String,
      default: 'none', // 'none' | 'asc' | 'desc'
      validator: (val) => ['none', 'asc', 'desc'].includes(val),
    },
    ariaLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      hoveredIndex: -1,
      defaultColors: ['#083073', '#1E55B5', '#4D81DB'],
    };
  },
  computed: {
    numericSize() {
      return parseInt(this.size, 10);
    },
    radius() {
      return this.numericSize / 2;
    },
    totalValue() {
      return this.data.reduce((acc, item) => acc + (Number(item.value) || 0), 0);
    },
    sortedData() {
      const dataCopy = [...this.data];
      if (this.sortData === 'asc') {
        dataCopy.sort((a, b) => a.value - b.value);
      } else if (this.sortData === 'desc') {
        dataCopy.sort((a, b) => b.value - a.value);
      }
      return dataCopy;
    },
    processedData() {
      let cumulativeValue = 0;
      const total = this.totalValue;

      if (total === 0) return [];

      // Primeira passagem: calcular posições iniciais
      const initialData = this.sortedData.map((item, index) => {
        const value = Number(item.value) || 0;
        const percent = (value / total) * 100;
        
        // Calcular ângulos em radianos
        // SVG coordenada Y é invertida e 0 graus é as 3 horas. Queremos começar as 12h (-90deg ou -PI/2)
        const startAngle = (cumulativeValue / total) * 2 * Math.PI;
        const endAngle = ((cumulativeValue + value) / total) * 2 * Math.PI;

        cumulativeValue += value;

        // Calcular Path do arco
        const path = this.makeArcPath(startAngle, endAngle, this.radius);

        // Calcular posição do label (centroide do arco)
        // Usamos um raio um pouco menor que o raio total, mas maior que o innerRadius para centralizar
        // Média entre raio externo e interno (se inner for 0, usa 0.6 do raio para ficar visualmente ok)
        const labelRadius = this.innerRadius > 0 
          ? (this.radius + this.innerRadius) / 2 
          : this.radius * 0.65;
        
        const midAngle = startAngle + (endAngle - startAngle) / 2;
        // Ajuste de -PI/2 para rotacionar o sistema para começar no topo (12h)
        const labelX = labelRadius * Math.cos(midAngle - Math.PI / 2);
        const labelY = labelRadius * Math.sin(midAngle - Math.PI / 2);

        // Calcular posições para legendas externas
        let legendX = 0;
        let legendY = 0;
        let legendTextX = 0;
        let legendTextAnchor = 'start';
        
        if (this.legendPosition === 'external') {
          const externalLegendRadius = this.radius + 30; // Offset de 30px do raio externo
          legendX = externalLegendRadius * Math.cos(midAngle - Math.PI / 2);
          legendY = externalLegendRadius * Math.sin(midAngle - Math.PI / 2);
          // Determinar se o texto fica à direita ou esquerda baseado na posição X
          const isRightSide = legendX >= 0;
          legendTextX = legendX + (isRightSide ? 12 : -12); // Offset do círculo colorido
          legendTextAnchor = isRightSide ? 'start' : 'end';
        }

        return {
          ...item,
          original: item, // referência ao objeto original
          percent: percent.toFixed(1),
          color: item.color || this.defaultColors[index % this.defaultColors.length],
          path,
          labelX,
          labelY,
          legendX,
          legendY,
          legendTextX,
          legendTextAnchor,
          midAngle,
        };
      });

      // Segunda passagem: corrigir sobreposições nas legendas externas
      if (this.legendPosition === 'external' && initialData.length > 1) {
        const minDistance = 18; // Distância mínima entre círculos (raio 6px * 2 + padding)
        const circleRadius = 6;
        const maxIterations = 10; // Limite de iterações para evitar loop infinito

        // Aplicar correção iterativa para resolver todas as sobreposições
        for (let iteration = 0; iteration < maxIterations; iteration++) {
          let hasOverlap = false;

          // Verificar todos os pares de círculos
          for (let i = 0; i < initialData.length; i++) {
            for (let j = i + 1; j < initialData.length; j++) {
              const circle1 = initialData[i];
              const circle2 = initialData[j];

              // Calcular distância entre os círculos
              const dx = circle2.legendX - circle1.legendX;
              const dy = circle2.legendY - circle1.legendY;
              const distance = Math.sqrt(dx * dx + dy * dy);

              // Se a distância for menor que o mínimo, ajustar posições
              if (distance < minDistance && distance > 0) {
                hasOverlap = true;
                
                // Calcular direção do vetor entre os círculos
                const unitX = dx / distance;
                const unitY = dy / distance;
                
                // Calcular offset necessário para separar os círculos
                const requiredOffset = (minDistance - distance) / 2;
                
                // Mover ambos os círculos para longe um do outro (simétrico)
                circle1.legendX -= unitX * requiredOffset;
                circle1.legendY -= unitY * requiredOffset;
                circle2.legendX += unitX * requiredOffset;
                circle2.legendY += unitY * requiredOffset;
                
                // Ajustar posições dos textos para acompanhar os círculos
                const isRightSide1 = circle1.legendX >= 0;
                circle1.legendTextX = circle1.legendX + (isRightSide1 ? 12 : -12);
                circle1.legendTextAnchor = isRightSide1 ? 'start' : 'end';
                
                const isRightSide2 = circle2.legendX >= 0;
                circle2.legendTextX = circle2.legendX + (isRightSide2 ? 12 : -12);
                circle2.legendTextAnchor = isRightSide2 ? 'start' : 'end';
              }
            }
          }

          // Se não houver mais sobreposições, parar
          if (!hasOverlap) break;
        }
      }

      return initialData;
    },
  },
  methods: {
    // Converte coordenadas polares em cartesianas
    polarToCartesian(centerX, centerY, radius, angleInRadians) {
      // Ajuste de -90 graus (-PI/2) para começar no topo
      return {
        x: centerX + (radius * Math.cos(angleInRadians - Math.PI / 2)),
        y: centerY + (radius * Math.sin(angleInRadians - Math.PI / 2))
      };
    },

    makeArcPath(startAngle, endAngle, radius) {
        // Se for um círculo completo (quase), desenhar dois arcos para evitar colapso do SVG arc
        if (endAngle - startAngle >= 2 * Math.PI - 0.0001) {
            endAngle = startAngle + 2 * Math.PI - 0.0001;
        }

        const start = this.polarToCartesian(0, 0, radius, endAngle);
        const end = this.polarToCartesian(0, 0, radius, startAngle);

        const largeArcFlag = endAngle - startAngle <= Math.PI ? "0" : "1";

        // M startX startY A radius radius 0 largeArcFlag 0 endX endY L 0 0 Z
        // L 0 0 conecta ao centro para fechar a fatia (necessário para preenchimento correto de pizza)
        // Se fosse donut puro via path, seria diferente, mas usaremos circle overlay para donut mask
        return [
            "M", start.x, start.y, 
            "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
            "L", 0, 0,
            "Z"
        ].join(" ");
    },
    
    onSliceClick(slice) {
      this.$emit('slice-click', slice.original);
    },
  },
};
</script>

<style scoped>
.retrospect-chart-container {
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.chart-wrapper {
  position: relative;
  /* Garante que o SVG não ultrapasse */
  flex-shrink: 0;
}

.chart-slice path {
  stroke: #fff;
  stroke-width: 0px;
}

.slice-label {
  fill: #fff;
  font-size: 12px;
  text-shadow: 0px 0px 3px rgba(0,0,0,0.5);
  pointer-events: none;
}
.slice-label.small {
    font-size: 10px;
}

/* Legenda Lateral */
.chart-legend {
  margin-left: 20px;
  max-width: 300px;
}

.chart-legend ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 4px;
  border-radius: 4px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 2px;
  margin-right: 10px;
  flex-shrink: 0;
}

.legend-text {
  font-size: 14px;
  color: #333;
  display: flex;
  flex-direction: column;
}

.legend-percent {
  font-size: 12px;
  color: #666;
}

.legend-color-circle {
  stroke: #fff;
  stroke-width: 1px;
}

.external-legend-text {
  fill: #333;
  font-size: 14px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.legend-percent-text {
  font-size: 12px;
  fill: #666;
}
</style>

